import { supabase } from '@/lib/supabase/client';
import { awardBadge, BadgeKey, hasBadge, normalizeBadgeScope, UserBadge } from '@/lib/badges';

export interface FounderChallenge {
  city: string;
  displayName: string;
  latitude: number;
  longitude: number;
  startedAt: number;
  expiresAt: number;
}

export interface FounderBadge extends UserBadge {
  city: string;
}

const ACTIVE_CHALLENGE_STORAGE_KEY = 'minibid_founder_challenge_active_v1';
const FOUNDER_BADGE_KEY: BadgeKey = 'founder_city';

export const FOUNDER_CHALLENGE_DEFAULT_HOURS = 72;

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function normalizeCity(value: string): string {
  return normalizeText(value);
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function extractCityFromDisplayName(displayName: string): string {
  const firstChunk = (displayName || '').split(',')[0]?.trim();
  return firstChunk || (displayName || '').trim();
}

function safeRead<T>(key: string, fallback: T): T {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed;
  } catch {
    return fallback;
  }
}

function safeWrite(key: string, value: unknown): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore write errors (private mode/quota)
  }
}

function mapDbChallenge(row: any): FounderChallenge {
  return {
    city: row.city,
    displayName: row.display_name,
    latitude: Number(row.latitude),
    longitude: Number(row.longitude),
    startedAt: new Date(row.started_at).getTime(),
    expiresAt: new Date(row.expires_at).getTime(),
  };
}

export async function hasFounderBadgeForCity(city: string, userId?: string | null): Promise<boolean> {
  const scope = normalizeBadgeScope(city);
  if (!scope) return false;
  return hasBadge({ userId, badgeKey: FOUNDER_BADGE_KEY, badgeScope: scope });
}

export async function getActiveFounderChallenge(userId?: string | null): Promise<FounderChallenge | null> {
  if (userId) {
    const { data, error } = await supabase
      .from('founder_city_challenges')
      .select('city, display_name, latitude, longitude, started_at, expires_at, status')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      console.warn('[founderProgram] getActiveFounderChallenge fallback to local due to error:', error);
    } else if (data) {
      const mapped = mapDbChallenge(data);
      if (mapped.expiresAt <= Date.now()) {
        await clearFounderChallenge(userId);
        return null;
      }
      return mapped;
    }
  }

  const challenge = safeRead<FounderChallenge | null>(ACTIVE_CHALLENGE_STORAGE_KEY, null);
  if (!challenge) return null;

  if (!Number.isFinite(challenge.expiresAt) || challenge.expiresAt <= Date.now()) {
    await clearFounderChallenge();
    return null;
  }

  return challenge;
}

export function getFounderChallengeRemainingMs(challenge: FounderChallenge | null): number {
  if (!challenge) return 0;
  return Math.max(0, challenge.expiresAt - Date.now());
}

export async function clearFounderChallenge(userId?: string | null): Promise<void> {
  if (userId) {
    const { error } = await supabase
      .from('founder_city_challenges')
      .update({ status: 'expired' })
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) {
      console.warn('[founderProgram] clearFounderChallenge remote error:', error);
    }
  }

  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(ACTIVE_CHALLENGE_STORAGE_KEY);
  } catch {
    // Ignore clear errors
  }
}

export async function startFounderChallenge(input: {
  userId?: string | null;
  city: string;
  displayName: string;
  latitude: number;
  longitude: number;
  durationHours?: number;
}): FounderChallenge | null {
  const city = (input.city || '').trim();
  if (!city) return null;

  if (await hasFounderBadgeForCity(city, input.userId)) {
    return null;
  }

  const durationHours = input.durationHours ?? FOUNDER_CHALLENGE_DEFAULT_HOURS;
  const startedAt = Date.now();
  const challenge: FounderChallenge = {
    city,
    displayName: input.displayName,
    latitude: input.latitude,
    longitude: input.longitude,
    startedAt,
    expiresAt: startedAt + Math.max(1, durationHours) * 60 * 60 * 1000,
  };

  if (input.userId) {
    const { error } = await supabase
      .from('founder_city_challenges')
      .upsert({
        user_id: input.userId,
        city: challenge.city,
        display_name: challenge.displayName,
        latitude: challenge.latitude,
        longitude: challenge.longitude,
        started_at: new Date(challenge.startedAt).toISOString(),
        expires_at: new Date(challenge.expiresAt).toISOString(),
        status: 'active',
        completed_at: null,
      }, { onConflict: 'user_id' });

    if (error) {
      console.warn('[founderProgram] startFounderChallenge remote upsert error:', error);
    }
  }

  safeWrite(ACTIVE_CHALLENGE_STORAGE_KEY, challenge);
  return challenge;
}

export async function maybeAwardFounderBadge(input: {
  userId?: string | null;
  storeId?: string;
  storeLocationDisplayName?: string | null;
  fallbackCity?: string | null;
}): FounderBadge | null {
  const activeChallenge = await getActiveFounderChallenge(input.userId);
  if (!activeChallenge) return null;

  const locationCity = input.storeLocationDisplayName
    ? extractCityFromDisplayName(input.storeLocationDisplayName)
    : (input.fallbackCity || '').trim();

  if (!locationCity) return null;

  if (normalizeCity(locationCity) !== normalizeCity(activeChallenge.city)) {
    return null;
  }

  const scope = normalizeBadgeScope(activeChallenge.city);
  const badge = await awardBadge({
    userId: input.userId,
    badgeKey: FOUNDER_BADGE_KEY,
    badgeScope: scope,
    city: activeChallenge.city,
    metadata: {
      challengeStartedAt: activeChallenge.startedAt,
      storeId: input.storeId,
      storeDisplayName: input.storeLocationDisplayName || undefined,
    },
  });

  await clearFounderChallenge(input.userId);

  if (!badge) {
    return null;
  }

  return {
    ...badge,
    city: activeChallenge.city,
  };
}
